import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * RALModal - Modal per l'inserimento del codice RAL
 * Appare quando si seleziona V1 o V2 come finitura (Verniciatura Speciale)
 */
const RALModal = ({ isOpen, onClose, onConfirm, finishType }) => {
    const [ralCode, setRalCode] = useState('');
    const [error, setError] = useState('');

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setRalCode('');
            setError('');
        }
    }, [isOpen]);

    // Handle RAL code validation
    const validateRAL = (code) => {
        // RAL codes are typically 4-digit numbers (e.g., RAL 9010, RAL 7016)
        // Some special codes may have letters (e.g., RAL 1000)
        const ralPattern = /^(RAL\s*)?[0-9]{4}$/i;
        return ralPattern.test(code.trim());
    };

    const handleInputChange = (e) => {
        const value = e.target.value.toUpperCase();
        setRalCode(value);
        setError('');
    };

    const SPECIAL_CODES = ['NK1', 'NK2', 'NK3', 'NKR1', 'NKR2', 'NKR3', 'BRONZO FARAONE', 'SILVER FARAONE'];

    const handleConfirm = () => {
        if (!ralCode.trim()) {
            setError('Inserire un codice RAL');
            return;
        }

        let cleanCode = ralCode.trim().toUpperCase();
        if (cleanCode.startsWith('RAL')) {
            cleanCode = cleanCode.replace(/^RAL\s*/i, '').trim();
        }

        // Check if it's a special Faraone code
        if (SPECIAL_CODES.includes(cleanCode)) {
            onConfirm(cleanCode);
            return;
        }

        // Standard RAL: 4 digits
        if (!/^[0-9]{4}$/.test(cleanCode)) {
            setError('Inserisci un codice RAL di 4 cifre (es. 9010) oppure un codice speciale (es. NK1, BRONZO FARAONE)');
            return;
        }

        const formattedCode = `RAL ${cleanCode}`;
        onConfirm(formattedCode);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleConfirm();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen) return null;

    const finishLabel = finishType === 'V1'
        ? 'Verniciatura Speciale - superiore a 120 metri'
        : 'Verniciatura Speciale - da 18 a 120 metri';

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-modal-enter"
                    onClick={e => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold">Inserisci Codice RAL</h3>
                                <p className="text-sm text-red-100 mt-1">{finishLabel}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-lg p-1 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        <div className="mb-4">
                            <p className="text-gray-600 text-sm mb-4">
                                Per la finitura <strong>{finishType}</strong> è necessario specificare il codice RAL OPACO desiderato o un codice speciale Faraone.
                            </p>

                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Codice RAL / Codice Speciale
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={ralCode}
                                    onChange={handleInputChange}
                                    onKeyDown={handleKeyDown}
                                    placeholder="9010 oppure NK1, BRONZO FARAONE..."
                                    maxLength={20}
                                    autoFocus
                                    className={`w-full px-4 py-3 border rounded-lg text-lg font-mono focus:outline-none focus:ring-2 ${error
                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                                        }`}
                                />
                            </div>
                            {error && (
                                <p className="mt-2 text-sm text-red-600">{error}</p>
                            )}
                        </div>

                        {/* Common RAL codes */}
                        <div className="mb-4">
                            <p className="text-xs text-gray-500 mb-2">Codici RAL comuni:</p>
                            <div className="flex flex-wrap gap-2">
                                {['8019', '9006', '7016', '1036'].map((code) => (
                                    <button
                                        key={code}
                                        onClick={() => setRalCode(code)}
                                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                                    >
                                        RAL {code}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Special Faraone codes */}
                        <div className="mb-6">
                            <p className="text-xs text-gray-500 mb-2">Codici speciali Faraone:</p>
                            <div className="flex flex-wrap gap-2">
                                {['NK1', 'NK2', 'NK3', 'NKR1', 'NKR2', 'NKR3', 'BRONZO FARAONE', 'SILVER FARAONE'].map((code) => (
                                    <button
                                        key={code}
                                        onClick={() => setRalCode(code)}
                                        className="px-3 py-1 text-xs bg-red-50 text-red-700 rounded-full hover:bg-red-100 transition-colors"
                                    >
                                        {code}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={handleConfirm}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                            >
                                Conferma
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx="true">{`
                @keyframes modalEnter {
                    from {
                        opacity: 0;
                        transform: scale(0.95) translateY(-10px);
                    }
                    to {
                        opacity: 1;
                        transform: scale(1) translateY(0);
                    }
                }

                .animate-modal-enter {
                    animation: modalEnter 0.2s ease-out forwards;
                }
            `}</style>
        </div>,
        document.body
    );
};

export default RALModal;
